// src/pages/OrderDetailsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
  Package,
  Truck,
  CheckCircle2,
  AlertCircle,
  Clock,
  UserCheck,
  Receipt,
  History,
  FileText,
} from 'lucide-react';
import { ordersAPI } from '../services/api';
import { Button } from '../components/UI/Button';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// === دوال مساعدة ===

const translateUnit = (unit: string | undefined, isRtl: boolean): string => {
  if (!unit) return isRtl ? 'وحدة' : 'unit';
  const map: Record<string, string> = {
    'كيلو': 'كيلو', kg: 'كجم',
    'قطعة': 'قطعة', piece: 'قطعة',
    'علبة': 'علبة', pack: 'علبة',
    'صينية': 'صينية', tray: 'صينية',
  };
  return map[unit] || unit;
};

const toArabicNumeralsWithDecimal = (num: number): string => {
  const str = num.toString();
  if (!str.includes('.')) {
    return str.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);
  }
  const [intPart, decPart] = str.split('.');
  const arabicInt = intPart.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);
  const arabicDec = decPart.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);
  return `${arabicInt}٫${arabicDec}`;
};

const toArabicNumerals = (str: string): string => {
  return str.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);
};

const formatCurrencyArabic = (amount: number): string => {
  const formatted = amount
    .toLocaleString('ar-SA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/,/g, '٬')
    .replace(/\./g, '٫');
  return `${toArabicNumerals(formatted)} ر.س`;
};

const formatCurrency = (amount: number, isRtl: boolean): string => {
  if (isRtl) {
    return formatCurrencyArabic(amount);
  }
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (dateString: string, isRtl: boolean): string => {
  return new Date(dateString).toLocaleString(isRtl ? 'ar-EG' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).replace(' AM', ' ص').replace(' PM', ' م');
};

const getTranslation = (key: string, isRtl: boolean): string => {
  const translations: Record<string, { ar: string; en: string }> = {
    order: { ar: 'طلب', en: 'Order' },
    summary: { ar: 'ملخص الطلب', en: 'Order Summary' },
    items: { ar: 'المنتجات', en: 'Products' },
    requested_total: { ar: 'إجمالي المطلوب', en: 'Requested Total' },
    final_total: { ar: 'المبلغ النهائي', en: 'Final Total' },
    received_amount: { ar: 'إجمالي المستلم', en: 'Received Amount' },
    requested: { ar: 'مطلوب', en: 'Requested' },
    received: { ar: 'مستلم', en: 'Received' },
    amount: { ar: 'القيمة', en: 'Amount' },
    rejected: { ar: 'مرفوض', en: 'Rejected' },
    notes: { ar: 'ملاحظات الطلب', en: 'Order Notes' },
    status_history: { ar: 'سجل الحالة', en: 'Status History' },
    status_pending: { ar: 'قيد الانتظار', en: 'Pending' },
    status_approved: { ar: 'تم الموافقة', en: 'Approved' },
    status_in_production: { ar: 'في الإنتاج', en: 'In Production' },
    status_completed: { ar: 'مكتمل', en: 'Completed' },
    status_in_transit: { ar: 'في النقل', en: 'In Transit' },
    status_delivered: { ar: 'تم التسليم', en: 'Delivered' },
    status_cancelled: { ar: 'ملغى', en: 'Cancelled' },
    product: { ar: 'المنتج', en: 'Product' },
    quantity: { ar: 'الكمية', en: 'Quantity' },
    unit_price: { ar: 'سعر الوحدة', en: 'Unit Price' },
    total_requested_qty: { ar: 'إجمالي الكمية المطلوبة', en: 'Total Requested Quantity' },
    total_received_qty: { ar: 'إجمالي الكمية المستلمة', en: 'Total Received Quantity' },
  };
  return translations[key]?.[isRtl ? 'ar' : 'en'] || key;
};

const loadAmiriFont = async (doc: jsPDF): Promise<boolean> => {
  const fontName = 'Amiri';
  const urls = {
    regular: 'https://raw.githubusercontent.com/aliftype/amiri/master/fonts/Amiri-Regular.ttf',
    bold: 'https://raw.githubusercontent.com/aliftype/amiri/master/fonts/Amiri-Bold.ttf',
  };
  const toBase64 = (buffer: ArrayBuffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return window.btoa(binary);
  };
  try {
    const [regRes, boldRes] = await Promise.all([fetch(urls.regular), fetch(urls.bold)]);
    if (!regRes.ok || !boldRes.ok) throw new Error('فشل تحميل خط Amiri');
    const [regBuffer, boldBuffer] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
    doc.addFileToVFS('Amiri-Regular.ttf', toBase64(regBuffer));
    doc.addFileToVFS('Amiri-Bold.ttf', toBase64(boldBuffer));
    doc.addFont('Amiri-Regular.ttf', fontName, 'normal');
    doc.addFont('Amiri-Bold.ttf', fontName, 'bold');
    return true;
  } catch (err) {
    console.warn('فشل تحميل خط Amiri. استخدام الخط الافتراضي.', err);
    return false;
  }
};

const formatPriceLine = (qty: number, unitPrice: number, total: number, isRtl: boolean): string => {
  if (!isRtl) {
    return `${qty} × ${unitPrice.toFixed(2)} = ${total.toFixed(2)} SAR`;
  }
  const qtyStr = toArabicNumeralsWithDecimal(qty);
  const unitStr = toArabicNumerals(unitPrice.toFixed(2).replace('.', '٫'));
  const totalStr = toArabicNumerals(total.toFixed(2).replace('.', '٫'));
  return `${qtyStr} × ${unitStr} = ${totalStr} ر.س`;
};

// ✅ دالة تصدير PDF مع عكس الأعمدة بدقة للعربية
const downloadOrderDetailPDF = async (order: any, isRtl: boolean) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const fontLoaded = await loadAmiriFont(doc);
  const fontFamily = fontLoaded ? 'Amiri' : 'helvetica';
  doc.setFont(fontFamily);

  // العنوان
  doc.setFontSize(22);
  doc.setFont(fontFamily, 'bold');
  doc.text(isRtl ? `فاتورة طلب رقم #${order.orderNumber}` : `Order Invoice #${order.orderNumber}`, pageWidth / 2, 25, { align: 'center' });

  // معلومات الطلب
  doc.setFontSize(12);
  doc.setFont(fontFamily, 'normal');
  const branchText = isRtl ? `الفرع: ${order.branch.displayName}` : `Branch: ${order.branch.displayName}`;
  const dateText = isRtl ? `التاريخ: ${order.date}` : `Date: ${order.date}`;
  doc.text(branchText, isRtl ? pageWidth - 20 : 20, 40, { align: isRtl ? 'right' : 'left' });
  doc.text(dateText, isRtl ? 20 : pageWidth - 20, 40, { align: isRtl ? 'left' : 'right' });

  const statusText = getTranslation(`status_${order.status}`, isRtl);
  doc.setTextColor(180, 90, 0);
  doc.setFont(fontFamily, 'bold');
  doc.text(isRtl ? `الحالة: ${statusText}` : `Status: ${statusText}`, pageWidth / 2, 48, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  // خط فاصل
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.5);
  doc.line(20, 58, pageWidth - 20, 58);

  // ✅ إنشاء العناوين والبيانات
  const baseHead = [
    getTranslation('product', isRtl),
    getTranslation('requested', isRtl),
    getTranslation('received', isRtl),
    getTranslation('unit_price', isRtl),
    getTranslation('amount', isRtl),
  ];

  const baseBody = order.items.map((item: any) => {
    const showReceived = (item.receivedQuantity > 0) || item.rejected;
    return [
      item.displayProductName,
      isRtl
        ? `${toArabicNumeralsWithDecimal(item.quantity)} ${item.displayUnit}`
        : `${item.quantity} ${item.displayUnit}`,
      showReceived
        ? (item.rejected
            ? (isRtl ? 'مرفوض' : 'Rejected')
            : (isRtl
                ? `${toArabicNumeralsWithDecimal(item.receivedQuantity)} ${item.displayUnit}`
                : `${item.receivedQuantity} ${item.displayUnit}`))
        : (isRtl ? '—' : '—'),
      formatCurrency(item.unitPrice, isRtl),
      item.rejected ? '٠٫٠٠ ر.س' : formatCurrency(item.receivedAmount, isRtl),
    ];
  });

  // ✅ عكس الترتيب للعربية فقط
  const tableHead = isRtl ? baseHead.reverse() : baseHead;
  const tableBody = isRtl ? baseBody.map((row: any[]) => row.reverse()) : baseBody;

  autoTable(doc, {
    head: [tableHead],
    body: tableBody,
    startY: 65,
    theme: 'grid',
    headStyles: {
      fillColor: [250, 230, 210],
      textColor: [30, 30, 30],
      fontStyle: 'bold',
      fontSize: 11,
      halign: 'center',
    },
    styles: {
      font: fontFamily,
      fontSize: 10,
      cellPadding: 6,
      halign: 'center', // ⬅️ جميع الخلايا متوسطة
    },
    columnStyles: isRtl
      ? {
          // [القيمة, سعر الوحدة, مستلم, مطلوب, المنتج]
          0: { cellWidth: 35, fontStyle: 'bold', textColor: [34, 139, 34] },
          1: { cellWidth: 35, fontStyle: 'bold' },
          2: { cellWidth: 30 },
          3: { cellWidth: 30 },
          4: { cellWidth: 60, halign: 'right' }, // ✅ عمود "المنتج" محاذي لليمين
        }
      : {
          // [المنتج, مطلوب, مستلم, سعر الوحدة, القيمة]
          0: { cellWidth: 60, halign: 'left' },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 35, fontStyle: 'bold' },
          4: { cellWidth: 35, fontStyle: 'bold', textColor: [34, 139, 34] },
        },
    margin: { left: 15, right: 15 },
  });

  let y = (doc as any).lastAutoTable.finalY + 6;

  // ملخص الكميات
  doc.setFontSize(13);
  doc.setFont(fontFamily, 'bold');
  doc.text(isRtl ? 'ملخص الكميات:' : 'Quantity Summary:', pageWidth / 2, y, { align: 'center' });
  y += 6;
  const qtySummary = isRtl
    ? [
        ['إجمالي المطلوب:', `${toArabicNumerals(order.totalRequestedQty.toString())} وحدة`],
        ...(order.status === 'delivered' ? [['إجمالي المستلم:', `${toArabicNumerals(order.totalReceivedQty.toString())} وحدة`]] : []),
      ]
    : [
        ['Total Requested:', `${order.totalRequestedQty} units`],
        ...(order.status === 'delivered' ? [['Total Received:', `${order.totalReceivedQty} units`]] : []),
      ];

  autoTable(doc, {
    body: qtySummary,
    startY: y,
    theme: 'plain',
    styles: { fontSize: 10, font: fontFamily, halign: 'center' },
    columnStyles: {
      0: { fontStyle: 'bold', halign: 'center' },
      1: { halign: 'center' },
    },
    margin: { left: 20, right: 20 },
  });

  y = (doc as any).lastAutoTable.finalY + 2;

  // الملخص المالي
  const summaryData = [
    [getTranslation('requested_total', isRtl), formatCurrency(order.total, isRtl)],
  ];
  if (order.adjustedTotal < order.total) {
    summaryData.push([getTranslation('final_total', isRtl), formatCurrency(order.adjustedTotal, isRtl)]);
  }


  autoTable(doc, {
    body: summaryData,
    startY: y,
    theme: 'plain',
    styles: { fontSize: 13, cellPadding: 6, font: fontFamily, halign: 'center' },
    columnStyles: {
      0: { fontStyle: 'bold', halign: 'center' },
      1: { fontStyle: 'bold', textColor: [34, 139, 34], halign: 'center' },
    },
    margin: { left: 20, right: 20 },
  });

  // الملاحظات
  if (order.notes) {
    y = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(12);
    doc.setFont(fontFamily, 'bold');
    doc.text(getTranslation('notes', isRtl) + ':', pageWidth / 2, y, { align: 'center' });
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(11);
    const splitNotes = doc.splitTextToSize(order.notes, pageWidth - 40);
    doc.text(splitNotes, pageWidth / 2, y + 8, { align: 'center' });
  }

  // التذييل
  doc.setFontSize(9);
  doc.setTextColor(150);
  const footerText = isRtl
    ? `نظام إدارة الجودياء – ${order.orderNumber}`
    : `Aljodia Management System – ${order.orderNumber}`;
  doc.text(footerText, pageWidth / 2, pageHeight - 12, { align: 'center' });

  const fileName = isRtl
    ? `${order.branch.displayName.replace(/\s+/g, '_')}_فاتورة_${order.orderNumber}.pdf`
    : `${order.branch.displayName.replace(/\s+/g, '_')}_Invoice_${order.orderNumber}.pdf`;
  doc.save(fileName);
  toast.success(isRtl ? 'تم تصدير الفاتورة بنجاح' : 'Invoice exported successfully');
};

// === المكون الرئيسي ===
export const OrderDetailsPage: React.FC = () => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await ordersAPI.getById(id);

      let totalRequestedQty = 0;
      let totalReceivedQty = 0;
      let receivedTotal = 0;
      let calculatedTotal = 0;

      const mappedItems = (res.items || []).map((item: any) => {
        const requestedQty = item.quantity || 0;
        const receivedQty = item.receivedQuantity || 0;
        const unitPrice = item.price || 0;
        const lineRequestedAmount = requestedQty * unitPrice;
        const lineReceivedAmount = (item.rejected || item.rejectedByBranch) ? 0 : receivedQty * unitPrice;

        totalRequestedQty += requestedQty;
        calculatedTotal += lineRequestedAmount;
        if (!item.rejected && !item.rejectedByBranch) {
          totalReceivedQty += receivedQty;
          receivedTotal += lineReceivedAmount;
        }

        return {
          _id: item._id,
          displayProductName: isRtl ? item.product?.name || 'منتج غير معروف' : item.product?.nameEn || item.product?.name || 'Unknown Product',
          quantity: requestedQty,
          receivedQuantity: receivedQty,
          unitPrice,
          requestedAmount: lineRequestedAmount,
          receivedAmount: lineReceivedAmount,
          price: item.price || 0,
          displayUnit: translateUnit(item.product?.unit, isRtl),
          rejected: !!item.rejected || !!item.rejectedByBranch,
          assignedTo: item.assignedTo
            ? { displayName: isRtl ? item.assignedTo.name : item.assignedTo.nameEn || item.assignedTo.name }
            : undefined,
        };
      });

      const mappedOrder = {
        ...res,
        id: res._id,
        orderNumber: res.orderNumber || 'N/A',
        total: calculatedTotal,
        adjustedTotal: res.adjustedTotal != null && res.adjustedTotal < calculatedTotal ? res.adjustedTotal : calculatedTotal,
        receivedTotal,
        totalRequestedQty,
        totalReceivedQty,
        branch: {
          displayName: isRtl ? res.branch?.name || 'غير معروف' : res.branch?.nameEn || res.branch?.name || 'Unknown',
        },
        date: formatDateTime(res.createdAt || new Date(), isRtl),
        items: mappedItems,
        statusHistory: (res.statusHistory || []).map((h: any) => ({
          ...h,
          changedAt: formatDateTime(h.changedAt || h.createdAt || new Date(), isRtl),
        })),
      };

      setOrder(mappedOrder);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'فشل جلب الطلب';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [id, isRtl]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleDownloadPDF = () => {
    if (order) downloadOrderDetailPDF(order, isRtl);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">{isRtl ? 'الطلب غير موجود' : 'Order not found'}</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <Button onClick={() => navigate('/orders')} size="lg">
          {isRtl ? 'العودة للطلبات' : 'Back to Orders'}
        </Button>
      </div>
    );
  }

  const isDelivered = order.status === 'delivered';
  const hasAdjustment = order.adjustedTotal < order.total;

  return (
    <div className="w-full min-h-screen mx-auto">
      <div className="mx-auto px-4 py-3">
        <div className="flex flex-col items-center text-center sm:flex-row sm:items-center sm:justify-between sm:text-start gap-3">
          <div className="flex items-center justify-center gap-3 w-full sm:w-auto">
            <div>
              <h1 className="text-lg font-bold text-gray-900">{isRtl ? `طلب #${order.orderNumber}` : `Order #${order.orderNumber}`}</h1>
              <p className="text-xs text-gray-500">{order.branch.displayName} • {order.date}</p>
            </div>
          </div>
          <Button onClick={handleDownloadPDF} variant="primary" size="sm" className="w-full sm:w-auto text-xs px-5">
            <FileText className="w-3.5 h-3.5" /> {isRtl ? 'تصدير PDF' : 'Export PDF'}
          </Button>
        </div>
      </div>

      <div className=" mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quantity Summary */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-200 text-center">
              <p className="text-indigo-700 font-medium text-xs">{getTranslation('total_requested_qty', isRtl)}</p>
              <p className="text-2xl font-bold text-indigo-900 mt-1">{order.totalRequestedQty}</p>
              {isDelivered && (
                <div className="mt-3 pt-3 border-t border-indigo-200">
                  <p className="text-emerald-700 font-medium text-xs">{getTranslation('total_received_qty', isRtl)}</p>
                  <p className={`text-2xl font-bold mt-1 ${order.totalReceivedQty < order.totalRequestedQty ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {order.totalReceivedQty}
                  </p>
                </div>
              )}
            </div>

            {/* Financial Summary */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-bold text-md flex items-center gap-3 mb-4">
                <Receipt className="w-6 h-6 text-amber-600" />
                {getTranslation('summary', isRtl)}
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{getTranslation('requested_total', isRtl)}</span>
                  <span className={`font-bold ${hasAdjustment ? 'line-through text-gray-400' : 'text-amber-600'}`}>{formatCurrency(order.total, isRtl)}</span>
                </div>
             {/* {isDelivered && order.receivedTotal > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm text-green-800">{getTranslation('received_amount', isRtl)}</span>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(order.receivedTotal, isRtl)}</span>
                    </div>
                  </div>
                )}*/}   {hasAdjustment && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{getTranslation('final_total', isRtl)}</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(order.adjustedTotal, isRtl)}</span>
                  </div>
                )}
               
              </div>
            </div>

            {/* Items List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h3 className="font-bold text-base flex items-center gap-2">
                  <Package className="w-4 h-4 text-amber-600" />
                  {getTranslation('items', isRtl)} ({order.items.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                {order.items.map((item: any) => {
                  const showReceivedLine = item.rejected || item.receivedQuantity > 0;
                  return (
                    <div key={item._id} className="p-4 hover:bg-gray-50 transition">
                      <div className="mb-4 flex flex-row justify-between">
                        <h4 className="font-semibold text-sm text-gray-900 mb-2">{item.displayProductName}</h4>
                        {item.assignedTo && (
                          <div className="flex items-center gap-1.5 text-emerald-600 text-xs mb-2">
                            <UserCheck className="w-3.5 h-3.5" />
                            {item.assignedTo.displayName}
                          </div>
                        )}
                      </div>
                      <div className="text-xs space-y-1.5">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{getTranslation('requested', isRtl)}:</span>
                          <span className="font-medium">{item.quantity} {item.displayUnit}</span>
                        </div>
                        {showReceivedLine && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{getTranslation('received', isRtl)}:</span>
                            <span className={`font-bold ${item.rejected ? 'text-red-600' : item.receivedQuantity < item.quantity ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {item.rejected ? getTranslation('rejected', isRtl) : `${item.receivedQuantity} ${item.displayUnit}`}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                          <span className="text-gray-600 text-xs">{isRtl ? 'السعر' : 'Price'}:</span>
                          {showReceivedLine && item.receivedQuantity > 0 ? (
                            <span className="font-bold text-emerald-600 text-sm">
                              {formatPriceLine(item.receivedQuantity, item.unitPrice, item.receivedAmount, isRtl)}
                            </span>
                          ) : (
                            <span className="font-bold text-amber-600 text-sm">
                              {formatPriceLine(item.quantity, item.unitPrice, item.requestedAmount, isRtl)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            {order.notes && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                <h4 className="font-bold text-amber-900 text-sm mb-1.5 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" /> {getTranslation('notes', isRtl)}
                </h4>
                <p className="text-amber-800 text-xs leading-relaxed">{order.notes}</p>
              </div>
            )}
          </div>

          {/* Status History */}
          <div className="lg:col-span-1 sticky top-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-20">
              <h3 className="font-bold text-base flex items-center gap-2 mb-4">
                <History className="w-4 h-4 text-amber-600" /> {getTranslation('status_history', isRtl)}
              </h3>
              <div className="space-y-4 relative">
                <div className={`absolute ${isRtl ? 'right-4' : 'left-4'} top-0 bottom-0 w-0.5 bg-gray-300`}></div>
                {order.statusHistory?.map((h: any, i: number, arr: any[]) => {
                  const isLast = i === arr.length - 1;
                  return (
                    <div key={i} className="relative flex gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 shrink-0 ${isLast ? 'bg-amber-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                        {h.status === 'delivered' ? <Truck className="w-3.5 h-3.5" /> : h.status === 'completed' ? <CheckCircle2 className="w-3.5 h-3.5" /> : h.status === 'pending' ? <Clock className="w-3.5 h-3.5" /> : <Package className="w-3.5 h-3.5" />}
                      </div>
                      <div className="text-xs">
                        <p className="font-semibold text-gray-800">{getTranslation(`status_${h.status}`, isRtl)}</p>
                        <p className="text-gray-500">{h.changedAt}</p>
                        {h.notes && <p className="text-gray-600 italic mt-0.5">{h.notes}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPage;